import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { sendSlackMessageTracked } from "../../../models/notifications";

// ─── Palette ──────────────────────────────────────────────────────────────────
// Background:  #ffffff / #f9fafb / #f3f4f6
// Accent:      #16a34a (green-700) — text, borders, icons
// Accent fill: #dcfce7 (green-100) — icon backgrounds
// Text:        #111827 headings, #374151 body, #6b7280 secondary

// ─── Icons ────────────────────────────────────────────────────────────────────

function Icon({ children }) {
    return (
        <div style={{ width: 44, height: 44, borderRadius: 12, background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, flexShrink: 0 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {children}
            </svg>
        </div>
    );
}

const CreditIcon    = () => <Icon><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></Icon>;
const RefreshIcon   = () => <Icon><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></Icon>;
const RetainIcon    = () => <Icon><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Icon>;
const BellIcon      = () => <Icon><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></Icon>;
const ShieldIcon    = () => <Icon><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></Icon>;
const TrendIcon     = () => <Icon><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></Icon>;
const AlertIcon     = () => <Icon><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></Icon>;
const CheckCircle   = () => <Icon><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></Icon>;

function CheckMark({ color = "#16a34a" }) {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
            <polyline points="20 6 9 17 4 12"/>
        </svg>
    );
}

function XMark() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
    );
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const label = { fontSize: 11, fontWeight: 700, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 10 };
const sectionHeading = { fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#111827", lineHeight: 1.2 };
const card = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, padding: 28 };
const bodyText = { fontSize: 13.5, color: "#6b7280", lineHeight: 1.7, margin: 0 };

// ─── Sections ─────────────────────────────────────────────────────────────────

function Hero({ onCTA }) {
    return (
        <section style={{ background: "#fff", padding: "96px 24px 80px", textAlign: "center", borderBottom: "1px solid #f3f4f6" }}>
            <div style={{ maxWidth: 720, margin: "0 auto" }}>
                <div style={{ display: "inline-block", background: "#dcfce7", borderRadius: 999, padding: "5px 14px", fontSize: 12, fontWeight: 700, color: "#16a34a", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 20 }}>
                    Store Credit Refunds
                </div>
                <h1 style={{ fontSize: "clamp(32px, 5.5vw, 58px)", fontWeight: 800, color: "#111827", lineHeight: 1.08, marginBottom: 20 }}>
                    Refunds that bring customers back
                </h1>
                <p style={{ fontSize: 17, color: "#6b7280", lineHeight: 1.7, maxWidth: 520, margin: "0 auto 40px" }}>
                    A cash refund sends money to the bank and ends the relationship. Store credit keeps the revenue in your store, gives customers instant resolution, and gives them a reason to return.
                </p>
                <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                    <button
                        onClick={onCTA}
                        style={{ padding: "14px 28px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
                    >
                        Start free trial
                    </button>
                    <a
                        href="/pricing"
                        style={{ padding: "14px 28px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 10, fontSize: 14, fontWeight: 600, textDecoration: "none" }}
                    >
                        See pricing
                    </a>
                </div>
            </div>
        </section>
    );
}

function StatsStrip() {
    const stats = [
        { value: "65%", label: "of customers spend more than their credit amount when they return" },
        { value: "3x", label: "faster resolution than waiting for a bank refund to clear" },
        { value: "40%", label: "of store-credit refund recipients repurchase within 30 days" },
        { value: "0%", label: "processing fees on credit vs the fee you lose on a cash refund" },
    ];
    return (
        <section style={{ background: "#f9fafb", borderBottom: "1px solid #f3f4f6", padding: "48px 24px" }}>
            <div style={{ maxWidth: 960, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 32, textAlign: "center" }}>
                {stats.map(s => (
                    <div key={s.label}>
                        <div style={{ fontSize: 32, fontWeight: 800, color: "#16a34a", marginBottom: 6 }}>{s.value}</div>
                        <div style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.5 }}>{s.label}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function ProblemSection() {
    const problems = [
        {
            icon: <AlertIcon />,
            title: "A cash refund ends the relationship",
            body: "When you return money to a customer's bank account, you lose the revenue and lose the customer. There's nothing pulling them back.",
        },
        {
            icon: <ShieldIcon />,
            title: "Processing fees don't get refunded",
            body: "Stripe and most payment processors keep the original transaction fee when you issue a refund. A $100 refund still cost you ~$3 to process.",
        },
        {
            icon: <TrendIcon />,
            title: "Most refund requests aren't brand rejection",
            body: "Wrong size, late delivery, product mismatch. These are fixable problems. How you respond determines whether the customer comes back or not.",
        },
    ];
    return (
        <section style={{ background: "#fff", padding: "80px 24px" }}>
            <div style={{ maxWidth: 900, margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: 56 }}>
                    <p style={label}>The Problem</p>
                    <h2 style={sectionHeading}>Standard refunds cost you twice</h2>
                    <p style={{ fontSize: 15, color: "#6b7280", maxWidth: 480, margin: "12px auto 0", lineHeight: 1.6 }}>
                        Once on the lost revenue, and again when that customer never returns.
                    </p>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
                    {problems.map(p => (
                        <div key={p.title} style={{ ...card, background: "#f9fafb" }}>
                            {p.icon}
                            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 8 }}>{p.title}</h3>
                            <p style={bodyText}>{p.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function HowItWorksSection() {
    const steps = [
        {
            num: "01",
            title: "Customer requests a refund",
            body: "A customer contacts support about a return or issue. This is the window to offer a better outcome than sending cash back to their bank.",
        },
        {
            num: "02",
            title: "You issue store credit in seconds",
            body: "Find the customer in Pabloo, enter the amount, and issue. Credit lands in their account immediately. You can optionally add a 10% bonus to make the offer more attractive than cash.",
        },
        {
            num: "03",
            title: "Customer gets instant resolution",
            body: "No waiting 5–7 business days. The credit is usable right away. Many customers prefer this when they know they'll shop with you again.",
        },
        {
            num: "04",
            title: "Customer comes back to spend it",
            body: "Their credit balance shows up automatically at checkout. They return sooner than they otherwise would, and 65% spend more than the credit amount in the same order.",
        },
    ];
    return (
        <section style={{ background: "#f9fafb", padding: "80px 24px" }}>
            <div style={{ maxWidth: 860, margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: 56 }}>
                    <p style={label}>How It Works</p>
                    <h2 style={sectionHeading}>Turn a refund into a retained customer</h2>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {steps.map(step => (
                        <div key={step.num} style={{ ...card, display: "flex", gap: 24, alignItems: "flex-start" }}>
                            <div style={{ fontSize: 20, fontWeight: 800, color: "#16a34a", minWidth: 36, lineHeight: 1, paddingTop: 2 }}>{step.num}</div>
                            <div>
                                <h3 style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 6 }}>{step.title}</h3>
                                <p style={bodyText}>{step.body}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ComparisonSection() {
    const rows = [
        { label: "Revenue stays inside your store",            credit: true,  cash: false },
        { label: "Customer has a reason to return",            credit: true,  cash: false },
        { label: "Instant resolution — no bank wait time",     credit: true,  cash: false },
        { label: "No processing fee lost on the refund",       credit: true,  cash: false },
        { label: "Option to add a bonus to the offer",         credit: true,  cash: false },
        { label: "Works for dispute and chargeback resolution", credit: true,  cash: false },
        { label: "Customer gets the money in cash",            credit: false, cash: true  },
    ];
    return (
        <section style={{ background: "#fff", padding: "80px 24px" }}>
            <div style={{ maxWidth: 820, margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: 48 }}>
                    <p style={label}>Comparison</p>
                    <h2 style={sectionHeading}>Store credit vs cash refund</h2>
                </div>
                <div style={{ border: "1px solid #e5e7eb", borderRadius: 16, overflow: "hidden" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 160px", borderBottom: "1px solid #f3f4f6", padding: "14px 24px", background: "#f9fafb" }}>
                        <div />
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#16a34a", textAlign: "center" }}>Store credit</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textAlign: "center" }}>Cash refund</div>
                    </div>
                    {rows.map((row, i) => (
                        <div
                            key={row.label}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 160px 160px",
                                padding: "13px 24px",
                                borderBottom: i < rows.length - 1 ? "1px solid #f3f4f6" : "none",
                                alignItems: "center",
                                background: i % 2 === 0 ? "#fff" : "#fafafa",
                            }}
                        >
                            <div style={{ fontSize: 13.5, color: "#374151" }}>{row.label}</div>
                            <div style={{ display: "flex", justifyContent: "center" }}>{row.credit ? <CheckMark /> : <XMark />}</div>
                            <div style={{ display: "flex", justifyContent: "center" }}>{row.cash ? <CheckMark color="#9ca3af" /> : <XMark />}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function FeaturesSection() {
    const features = [
        { icon: <CreditIcon />,   title: "Issue credit in under 10 seconds",       body: "Find the customer in Pabloo, enter the amount, and issue. The credit appears in their account immediately with no additional steps." },
        { icon: <BellIcon />,     title: "Automatic credit notification",           body: "Pabloo emails the customer confirming their balance and how to use it on their next order." },
        { icon: <RetainIcon />,   title: "Optional bonus credit",                   body: "Offer 10% extra to customers who choose store credit over cash. Many take it — and it costs you less than losing the sale entirely." },
        { icon: <RefreshIcon />,  title: "Applies automatically at checkout",       body: "No code required. The customer's balance appears at checkout and applies in one click." },
        { icon: <ShieldIcon />,   title: "Full audit trail",                        body: "Every credit issuance is logged with a timestamp, amount, reason, and the team member who issued it." },
        { icon: <CheckCircle />,  title: "Works for chargebacks too",               body: "The same credit system handles customer service refunds and pre-chargeback dispute resolution from one place." },
    ];
    return (
        <section style={{ background: "#f9fafb", padding: "80px 24px" }}>
            <div style={{ maxWidth: 960, margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: 56 }}>
                    <p style={label}>Features</p>
                    <h2 style={sectionHeading}>Everything you need to run store credit refunds</h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
                    {features.map(f => (
                        <div key={f.title} style={card}>
                            {f.icon}
                            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 8 }}>{f.title}</h3>
                            <p style={bodyText}>{f.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function UseCasesSection() {
    const cases = [
        {
            title: "Wrong size or variant",
            body: "The most common refund reason on Shopify. Issue credit immediately so the customer can reorder the correct size. Faster than a return, and they stay in your store.",
        },
        {
            title: "Shipping damage or delay",
            body: "When a package arrives damaged or significantly late, offering store credit shows accountability and keeps the customer from disputing the charge with their bank.",
        },
        {
            title: "Product didn't match the description",
            body: "Issue partial store credit as a goodwill gesture. The customer keeps the product and the relationship stays intact.",
        },
        {
            title: "Proactive service recovery",
            body: "For any experience that went wrong before a refund was requested, a store credit offer often resolves the issue before a review or chargeback is filed.",
        },
    ];
    return (
        <section style={{ background: "#fff", padding: "80px 24px" }}>
            <div style={{ maxWidth: 860, margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: 48 }}>
                    <p style={label}>Use Cases</p>
                    <h2 style={sectionHeading}>When to offer credit instead of cash</h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
                    {cases.map(c => (
                        <div key={c.title} style={{ ...card, background: "#f9fafb" }}>
                            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 10 }}>{c.title}</h3>
                            <p style={bodyText}>{c.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function RelatedSection() {
    const links = [
        { title: "Cashback rewards",       body: "Issue store credit after every purchase automatically. Drives repeat orders without discount codes.",                               href: "/solutions/cashback" },
        { title: "Chargeback prevention",  body: "Resolve disputes before they reach the bank. Avoids fees and protects your payment processor standing.",                          href: "/solutions/shopify/no-more-chargebacks" },
        { title: "Cart abandonment",       body: "Offer store credit to shoppers who leave without checking out. Brings them back without a public discount.",                      href: "/solutions/shopify-cart-abandonment-solutions" },
    ];
    return (
        <section style={{ background: "#f9fafb", padding: "80px 24px" }}>
            <div style={{ maxWidth: 860, margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: 48 }}>
                    <p style={label}>Related</p>
                    <h2 style={sectionHeading}>More ways to use store credit</h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
                    {links.map(l => (
                        <a key={l.title} href={l.href} style={{ ...card, textDecoration: "none", display: "block" }}>
                            <h3 style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 10 }}>{l.title}</h3>
                            <p style={{ ...bodyText, fontSize: 13 }}>{l.body}</p>
                            <p style={{ fontSize: 12, color: "#16a34a", fontWeight: 600, marginTop: 14, marginBottom: 0 }}>Learn more →</p>
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
}

function PricingSection({ onCTA }) {
    const plans = [
        {
            name: "Starter",
            price: "$49",
            annualPrice: "$39",
            features: ["1,000 customer accounts", "1 Shopify store", "5,000 credit events / mo", "Unlimited credit issued", "Unlimited team accounts", "Unlimited email notifications", "Chat support"],
            cta: "Get started",
            highlight: false,
        },
        {
            name: "Growth",
            price: "$149",
            annualPrice: "$119",
            features: ["10,000 customer accounts", "3 Shopify stores", "16,000 credit events / mo", "2,000 SMS / mo", "Unlimited credit issued", "Unlimited team accounts", "Full API access", "24/7 priority support"],
            cta: "Get started",
            highlight: true,
        },
    ];
    const [annual, setAnnual] = useState(true);

    return (
        <section style={{ background: "#fff", padding: "80px 24px", borderTop: "1px solid #f3f4f6" }}>
            <div style={{ maxWidth: 760, margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: 40 }}>
                    <p style={label}>Pricing</p>
                    <h2 style={sectionHeading}>Flat rate. No surprises.</h2>
                    <p style={{ fontSize: 15, color: "#6b7280", marginTop: 10, marginBottom: 28 }}>
                        One fixed price per month. Unlimited credit issued, unlimited team members.
                    </p>
                    <div style={{ display: "inline-flex", alignItems: "center", background: "#f3f4f6", borderRadius: 999, padding: 4 }}>
                        {[["monthly", "Monthly"], ["annually", "Annual (save 20%)"]].map(([val, txt]) => (
                            <button
                                key={val}
                                onClick={() => setAnnual(val === "annually")}
                                style={{
                                    padding: "7px 18px", borderRadius: 999, border: "none",
                                    fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                                    background: (val === "annually") === annual ? "#fff" : "transparent",
                                    color: (val === "annually") === annual ? "#111827" : "#6b7280",
                                    boxShadow: (val === "annually") === annual ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                                    transition: "all 0.15s",
                                }}
                            >
                                {txt}
                            </button>
                        ))}
                    </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
                    {plans.map(plan => (
                        <div
                            key={plan.name}
                            style={{
                                borderRadius: 18,
                                padding: 28,
                                border: plan.highlight ? "2px solid #16a34a" : "1px solid #e5e7eb",
                                background: plan.highlight ? "#f0fdf4" : "#fff",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <p style={{ fontSize: 11, fontWeight: 700, color: plan.highlight ? "#16a34a" : "#6b7280", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>
                                {plan.name}
                            </p>
                            <div style={{ display: "flex", alignItems: "flex-end", gap: 4, marginBottom: 4 }}>
                                <span style={{ fontSize: 40, fontWeight: 800, color: "#111827", lineHeight: 1 }}>
                                    {annual ? plan.annualPrice : plan.price}
                                </span>
                                <span style={{ fontSize: 13, color: "#9ca3af", marginBottom: 6 }}> / mo</span>
                            </div>
                            <p style={{ fontSize: 11.5, color: "#9ca3af", marginBottom: 24 }}>
                                {annual ? "billed annually" : "billed monthly"}
                            </p>
                            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", display: "flex", flexDirection: "column", gap: 10, flex: 1 }}>
                                {plan.features.map(f => (
                                    <li key={f} style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 13, color: "#374151" }}>
                                        <CheckMark /> {f}
                                    </li>
                                ))}
                            </ul>
                            <button
                                onClick={onCTA}
                                style={{
                                    width: "100%", padding: 12, fontSize: 13, fontWeight: 700, borderRadius: 10, border: "none", cursor: "pointer",
                                    background: plan.highlight ? "#16a34a" : "#111827",
                                    color: "#fff",
                                    transition: "opacity 0.15s",
                                }}
                                onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                            >
                                {plan.cta}
                            </button>
                        </div>
                    ))}
                </div>
                <p style={{ textAlign: "center", fontSize: 12, color: "#9ca3af", marginTop: 20 }}>
                    No setup fees · 14-day free trial · Cancel anytime
                </p>
            </div>
        </section>
    );
}

function FinalCTA({ onCTA }) {
    return (
        <section style={{ background: "#f0fdf4", borderTop: "1px solid #bbf7d0", padding: "80px 24px", textAlign: "center" }}>
            <div style={{ maxWidth: 540, margin: "0 auto" }}>
                <h2 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, color: "#111827", lineHeight: 1.2, marginBottom: 16 }}>
                    Keep the revenue. Keep the customer.
                </h2>
                <p style={{ fontSize: 15, color: "#6b7280", lineHeight: 1.6, marginBottom: 32 }}>
                    14-day free trial. Works with your existing Shopify store. Issue your first credit in under a minute.
                </p>
                <button
                    onClick={onCTA}
                    style={{ padding: "15px 32px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer" }}
                >
                    Start free trial
                </button>
            </div>
        </section>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function StoreCreditRefundsComponent() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
        sendSlackMessageTracked("Lead is visiting the Store Credit Refunds page.", "#website-updates");
    }, []);

    const handleCTA = () => { window.location.href = "https://app.pabloo.com/sign-up"; };

    return (
        <div style={{ background: "#fff", fontFamily: "'Inter', system-ui, sans-serif" }}>
            <Hero onCTA={handleCTA} />
            <StatsStrip />
            <ProblemSection />
            <HowItWorksSection />
            <ComparisonSection />
            <FeaturesSection />
            <UseCasesSection />
            <RelatedSection />
            <PricingSection onCTA={handleCTA} />
            <FinalCTA onCTA={handleCTA} />
        </div>
    );
}
